import { evidenciasService, UploadFolderResponse } from '@/services/evidencias.service';
import { useCallback, useState } from 'react';

interface UseEvidenciasResult {
  uploadEvidencia: (formData: FormData) => Promise<void>;
  uploadFolder: (files: FileList | File[]) => Promise<UploadFolderResponse>;
  deleteEvidencia: (evidenciaId: number) => Promise<void>;
  isDeleting: boolean;
  isUploading: boolean;
}

export function useEvidencias(nit: string, asesoriaId: number, onRefetch?: () => void): UseEvidenciasResult {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const uploadEvidencia = useCallback(
    async (formData: FormData) => {
      formData.append('asesoriaId', String(asesoriaId));
      setIsUploading(true);
      try {
        await evidenciasService.upload(nit, formData);
        onRefetch?.();
      } catch (err) {
        throw err instanceof Error ? err : new Error('Error al subir evidencia');
      } finally {
        setIsUploading(false);
      }
    },
    [nit, asesoriaId, onRefetch]
  );

  const uploadFolder = useCallback(
    async (files: FileList | File[]): Promise<UploadFolderResponse> => {
      const fileArray = Array.from(files);
      if (fileArray.length === 0) throw new Error('No hay archivos para subir');

      const formData = new FormData();
      formData.append('asesoriaId', String(asesoriaId));
      for (const file of fileArray) {
        formData.append('file[]', file);
        formData.append('name[]', file.name.replace(/\.[^/.]+$/, ''));
        formData.append('relativePath[]', (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name);
      }

      setIsUploading(true);
      try {
        const result = await evidenciasService.uploadMultiple(nit, formData);
        onRefetch?.();
        return result;
      } catch (err) {
        throw err instanceof Error ? err : new Error('Error al subir carpeta');
      } finally {
        setIsUploading(false);
      }
    },
    [nit, asesoriaId, onRefetch]
  );

  const deleteEvidencia = useCallback(
    async (evidenciaId: number) => {
      try {
        setIsDeleting(true);
        await evidenciasService.eliminar(nit, evidenciaId);
        onRefetch?.();
      } catch (err) {
        throw err instanceof Error ? err : new Error('Error al eliminar evidencia');
      } finally {
        setIsDeleting(false);
      }
    },
    [nit, onRefetch]
  );

  return {
    uploadEvidencia,
    uploadFolder,
    deleteEvidencia,
    isDeleting,
    isUploading,
  };
}
